<template>
    <div ref="galleryModal" class="gallery-modal" :class="{'is-modern': galleryVersion === 'modern', 'is-legacy': galleryVersion === 'legacy' }" v-if="isModalOpen" role="dialog" aria-modal="true" :style="this.defaultModalPosition">
        <div class="gallery-modal__header" >
            <span v-if="galleryVersion == 'modern' && pageWidth > 768" class="gallery-modal__active-caption" v-html="displayHtmLChar(carouselActiveItemMessaging)"></span>
            <div>
                <button @click="modalClose" class="gallery-modal__close-btn" :class="{'focus-visible': galleryVersion == 'legacy'}" ref="galleryCloseButton"><span class="sr-only">{{labels.filterCloseButtonText }}</span></button>
            </div>
        </div>

        <div class="gallery-modal__carousel" ref="slickCarousel" :class="{'single-item': getFilteredItems.length == 1}">
            <div v-for="(item) in getFilteredItems" v-bind:key="item.id" :data-modal-carousel-item-id="item.id" class="gallery-modal__carousel-outer-item" :class="{ 'is-video': item.isVideo }">
                <template v-if="item.isVideo">
                    <figure>
                        <div class="video-wrapper">
                            <gallery-video :is-priority="item.id === activeItemId" :item="item" mode="full" v-bind:key="`video-${item.id}-modal`" />
                        </div>
                        <figcaption v-if="item.caption" class="gallery-modal__carousel-caption" :class="{'d-none': (galleryVersion === 'modern' && pageWidth > 768) }" :aria-hidden="(galleryVersion == 'modern' && pageWidth > 768)" v-html="`<p>${displayHtmLChar(item.caption)}</p>`"></figcaption>
                    </figure>
                </template>
                <template v-else>
                    <figure>
                        <picture data-type="full" data-clicked-item="true" v-if="item.id === activeItemId"><!-- If we are the item that was clicked on, don't lazy load-->
                            <source media="(min-width: 1440px)" :srcset="`${item.image}?width=1200`" />
                            <source media="(min-width: 1200px) and (max-width: 1439px)" :srcset="`${item.image}?width=1100`" />
                            <source media="(min-width: 992px) and (max-width: 1219px)" :srcset="`${item.image}?width=1000`" />
                            <source media="(min-width: 768px) and (max-width: 991px)" :srcset="`${item.image}?width=850`" />
                            <source media="(max-width: 768px)" :srcset="`${item.image}?width=600`" />
                            <img class="asset-img" decoding="async" fetchpriority="high" :src="`${item.image}?width=700`" :width="item.imageWidth" :height="item.imageHeight" :alt="item.imageAlternativeText" :aria-label="item.imageAlternativeText" />
                        </picture>
                        <picture data-type="full" data-clicked-item="false" v-else>
                            <source media="(min-width: 1440px)" :data-srcset-observer="`${item.image}?width=1200`" />
                            <source media="(min-width: 1200px) and (max-width: 1439px)" :data-srcset-observer="`${item.image}?width=1100`" />
                            <source media="(min-width: 992px) and (max-width: 1219px)" :data-srcset-observer="`${item.image}?width=1000`" />
                            <source media="(min-width: 768px) and (max-width: 991px)" :data-srcset-observer="`${item.image}?width=850`" />
                            <source media="(max-width: 768px)" :data-srcset-observer="`${item.image}?width=600`" />
                            <img class="asset-img" decoding="async" fetchpriority="low" src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" :data-src-observer="`${item.image}?width=700`" :width="item.imageWidth" :height="item.imageHeight" :alt="item.imageAlternativeText" :aria-label="item.imageAlternativeText" />
                        </picture>
                        <figcaption v-if="item.caption" class="gallery-modal__carousel-caption" :class="{'d-none': (galleryVersion === 'modern' && pageWidth > 768)}" :aria-hidden="(galleryVersion == 'modern' && pageWidth > 768)" v-html="`<p>${displayHtmLChar(item.caption)}</p>`"></figcaption>
                    </figure>
                </template>
            </div>
        </div>

        <div aria-live="polite" class="gallery-modal__announce-text">{{ carouselActiveItemMessaging }}</div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
    import slick from 'slick-carousel';
    import $ from 'jquery';
    import { debounce } from 'lodash';

    import { calculateEnterExitModalPosition, allowPageScrolling } from '../mixins/helpers';


    import galleryVideo from './galleryVideo.vue';
    
    let debouncedCalcPeeks;

    export default {
        name: 'GalleryModal',
        props: [''],
        data() {
            return {
                slickInstance: null,
                activeIndex: null,
                carouselActiveItemMessaging: null,
                currentItemId: null,
                currentElem: null,
            }
        },

        computed: {
            ...mapGetters('gallery', [
                'getFilteredItems',
                
            ]),
            ...mapState('translations', [
                'labels',
            ]),
            ...mapState('gallery', [
                'activeItem',
                'autoplay',
                'isModalOpen',
                'galleryVersion',
                'modalPosition',
                'modalAnimationLength',
                'activeItemId',
            ]),
            pageWidth() {
                return window.innerWidth;
            },

            defaultModalPosition() {
                // We want to set a default position being very far offscreen so that when the markup is initially shown for a splitsecond until new calculated position
                // is set, we still show it offscreen
                return this.galleryVersion == 'modern' ? 'transform: translateY(100000px);' : '';
            }
        },

        beforeDestroy() {
            setTimeout(() => {
                this.destroyCarousel();
            }, 1250); // This delay is to allow the modal closing animation to finish

            document.removeEventListener('keydown', this.onKeyDownInModal);
            window.removeEventListener('resize', debouncedCalcPeeks, false);
        },

        mounted() {
            this.currentItemId = this.activeItemId;

            this.$nextTick(() => {
                this.createCarousel();
            });

            setTimeout(() => {
                this.$refs.galleryCloseButton.focus();
            }, 500);

            document.addEventListener('keydown', this.onKeyDownInModal);

            // When on Modern version, and on browser resize, recalculate the carousel peeks in the modal to fix overlap issue after resize
            if (this.galleryVersion == 'modern') {
                debouncedCalcPeeks = debounce(this.calculateCarouselPeeks, 150);
                window.addEventListener('resize', debouncedCalcPeeks, false);
            }
        },

        methods: {
            ...mapMutations('gallery', [
                'updateModalOpened',
                'updateActiveItem',
                'updateModalAnimating',
            ]),

            ...mapActions('gallery', [
                'toggleModernModal',
            ]),

            onKeyDownInModal(event) {
                if (event.code === 'ArrowLeft') {
                    event.preventDefault();
                    this.slickInstance.slick('slickPrev');
                } else if (event.code === 'ArrowRight') {
                    event.preventDefault();
                    this.slickInstance.slick('slickNext');
                } else if (event.code === "Escape") {
                    event.preventDefault();
                    this.modalClose();
                } else if (event.key === 'Tab' || event.keyCode === 39) {
                    // Focus trap for modal by only allowing a select set of elements to be tabbed

                    const focusableEls = this.$refs.galleryModal.querySelectorAll('a[href]:not([disabled]), button:not([disabled])');
                    const firstFocusableEl = focusableEls[0];
                    const lastFocusableEl = focusableEls[focusableEls.length - 1];

                    if (event.shiftKey) /* shift + tab */ {
                        if (document.activeElement === firstFocusableEl) {
                            lastFocusableEl.focus();
                            event.preventDefault();
                        }
                    } else /* tab */ {
                        if (document.activeElement === lastFocusableEl) {
                            firstFocusableEl.focus();
                            event.preventDefault();
                        }
                    }
                }
            },

            modalClose() {
                this.updateModalAnimating(true);
                document.body.classList.remove('gallery-modal-open');
                document.body.classList.add('gallery-modal-close');

                // Re-allow page scrolling
                if (this.galleryVersion === 'legacy') {
                    const scrollY = parseInt(document.body.dataset.storedPosition || 0) * -1;

                    document.body.style.position = '';
                    document.body.style.top = '';

                    setTimeout(() => {
                        window.scrollTo({ left: 0, top: scrollY, behavior: "instant" });
                    }, 10);

                } else if (this.galleryVersion === 'modern') {
                    const scrollY = parseInt(document.body.dataset.storedPosition || 0) * -1;
                    document.body.style.transform = '';
                    document.body.style.width = '';

                    document.body.style.position = '';
                    window.scrollTo({ left: 0, top: scrollY, behavior: "instant" });

                    setTimeout(() => {
                        // document.querySelector('.gallery-modal').style.transform = ''; // Reset modal position to default

                        // Set modal to start location
                        document.querySelector('.gallery-modal').style.transform = 'translateY(' + calculateEnterExitModalPosition() + 'px)';
                    }, 10);

                    this.toggleModernModal(false);
                }

                setTimeout(() => {
                    document.documentElement.classList.remove('overflow-hidden', 'video-fullscreened', 'gallery-modal-open');
                }, 250);


                setTimeout(() => {
                    this.updateModalOpened(false);
                    this.updateModalAnimating(false);
                    this.updateActiveItem(null);

                    allowPageScrolling();
                    this.focusOnCurrentItem();
                }, this.modalAnimationLength);

                // Reset audioeye position 
                setTimeout(() => {
                    document.body.classList.remove('gallery-modal-close');
                }, 4050);
            },

            focusOnCurrentItem() {
                const newCurrentId = document.querySelector('.slick-current').querySelector('.gallery-modal__carousel-outer-item').getAttribute('data-modal-carousel-item-id');
                
                if (newCurrentId == this.currentItemId) return; // We are already viewing the current item, so dont scroll
                
                this.currentItemId = newCurrentId;
                this.currentElem = document.querySelector(`#gallery-item-${this.currentItemId}`);
                this.currentElem.focus();

                // We need loco scroll to go to the item
                if (this.galleryVersion === 'modern' && typeof window['scrolInstance'] !== 'undefined' && window.innerWidth >= 992) {
                    this.locoscrollToListingItem(this.currentElem);                   
                }
            },

            locoscrollToListingItem(elem) {
                const headerHeight = document.querySelector('.header')?.clientHeight ?? 0;
                window['scrolInstance'].scrollTo(elem, { offset: -headerHeight - 15 }); // Extra 15px so its not against edge of screen
            },

            getActiveItemSliceIndex() {
                const index = this.getFilteredItems.findIndex(item => item.id === this.activeItem.id);
                if (index == null) throw new Error("Could not find active slide index", this.activeItem);
                return index;
            },

            destroyCarousel() {
                this.slickInstance.off('beforeChange', this.onBeforeCarouselChange);
                this.slickInstance.off('afterChange', this.onAfterCarouselChange);
                this.slickInstance.slick('unslick');
            },


            updateSlideToPeakImage(otherSlide, type) {
                const peakAmount = 30;
                const directionOperator = (type == 'previous') ? '' : '-';
                const slideIsVideo = otherSlide.querySelector('.is-video') ? true : false;
                const slideAssetSelector = slideIsVideo ? '.asset-vid' : '.asset-img';
                const otherSlideAsset = otherSlide.querySelector(slideAssetSelector);

                if (otherSlideAsset == null) return;

                const otherSlideHalfWidth = otherSlide.clientWidth / 2;
                const halfPrevImageWidth = (otherSlideAsset.clientWidth - peakAmount) / 2;
                const otherSlideTranslation = otherSlideHalfWidth - halfPrevImageWidth;

                otherSlide.style.transform = `translate(${directionOperator}${otherSlideTranslation}px, 0)`;
                otherSlide.classList.add('has-peek');
            },

            createCarousel() {
                this.slickInstance = $(this.$refs.slickCarousel);

                this.slickInstance.on('beforeChange', this.onBeforeCarouselChange);
                this.slickInstance.on('afterChange', this.onAfterCarouselChange);

                // Extra slick events we don't need at this stage

                // $slick.on('beforeChange', this.onBeforeChange);
                // $slick.on('breakpoint', this.onBreakpoint);
                // $slick.on('destroy', this.onDestroy);
                // $slick.on('edge', this.onEdge);
                // $slick.on('init', this.onInit);
                // $slick.on('reInit', this.onReInit);
                // $slick.on('setPosition', this.onSetPosition);
                // $slick.on('swipe', this.onSwipe);
                // $slick.on('lazyLoaded', this.onLazyLoaded);
                // $slick.on('lazyLoadError', this.onLazyLoadError);


                if (this.galleryVersion === 'legacy') {

                    this.slickInstance.slick({
                        infinite: true,
                        slidesToShow: 1,
                        slidesToScroll: 1,
                        variableWidth: false,
                        centerMode: true,
                        centerPadding: '0%',
                        initialSlide: this.getActiveItemSliceIndex(),
                    });

                } else {
                    this.slickInstance.slick({
                        infinite: true,
                        centerMode: true,
                        slidesToShow: 1,
                        variableWidth: false,
                        CssEase: 'cubic-bezier: 0.9, 0',
                        centerPadding: '0%',
                        centerMode: true,
                        slidesToScroll: 1,
                        initialSlide: this.getActiveItemSliceIndex(),
                    });
                }

                // Pause all videos including any slide clones, and only active if that has autoplay false, otherwise leave it to be played
                this.slickInstance.get(0).slick.$slideTrack.get(0).querySelectorAll('video').forEach(video => {
                    const activeSlide = video.closest('.slick-active');

                    if ((activeSlide && this.activeItem.autoplay === false) || activeSlide == null) {
                        setTimeout(() => {
                            video.pause();
                        }, 50);
                    }
                });

                // If the active item is a video and should autoplay, we need to trigger the video to play using a custom event
                if (this.activeItem.isVideo && this.activeItem.autoplay) {
                    const customChangeEvent = new CustomEvent('carouselSlideChange', { detail: { state: 'new-slide' } });
                    const activeIndex = this.slickInstance.get(0).slick.currentSlide;
                    const activeVideo = this.slickInstance.get(0).slick.$slides.get(activeIndex).querySelector('video');
                    if (activeVideo) {
                        setTimeout(() => {
                            activeVideo.dispatchEvent(customChangeEvent); // Triggers play
                        }, 75);
                    }
                }

                this.carouselActiveItemMessaging = this.activeItem.caption ? this.activeItem.caption : '';

                const currentSlide = this.$refs.slickCarousel.querySelector('.slick-current');
                if (currentSlide) {
                    this.lazyLoadSlide(currentSlide);
                }

                this.lazyLoadSurroundingSlides(true);

                const that = this;

                if (this.galleryVersion == 'modern') {
                    let checkCount = 0;
                    const maxCheckCount = 40;

                    function checkForSiblingsLoaded() {
                        const previousSlideElem = currentSlide.previousElementSibling;
                        const nextSlideElem = currentSlide.nextElementSibling;

                        const previousSlideHasLoaded = previousSlideElem ? previousSlideElem.classList.contains('slide-custom-lazy-loaded') : null;
                        const nextSlideHasLoaded = nextSlideElem ? nextSlideElem.classList.contains('slide-custom-lazy-loaded') : null;

                        const previousSlideIsVideo = previousSlideElem ? previousSlideElem.querySelector('.is-video') : false;
                        const nextSlideIsVideo = nextSlideElem ? nextSlideElem.querySelector('.is-video') : false;

                        // We need to check if the previous or next slides are clones, if they are they wont load properly so we need to continue peeking code
                        if (((!previousSlideIsVideo && previousSlideHasLoaded) || previousSlideIsVideo) && ((!nextSlideIsVideo && nextSlideHasLoaded) || nextSlideIsVideo)) {
                            that.calculateCarouselPeeks();
                        } else if (!previousSlideElem || !nextSlideElem) {
                            return;
                        } else {
                            if (checkCount < maxCheckCount) {
                                setTimeout(() => {
                                    checkForSiblingsLoaded();
                                }, 250);
                            }
                        }

                        checkCount++;
                    }

                    setTimeout(() => {
                        checkForSiblingsLoaded();
                    }, 150); // This timeout is to allow for the left and right slides to load their images. This may need to become a callback with slow loading checks
                }
            },

            calculateCarouselPeeks() {
                if (window.innerWidth < 768 || this.galleryVersion != 'modern') return; // Only run peeks on tablet and desktop and when modern version
                
                const activeSlideElem = this.$refs.slickCarousel.querySelector('.slick-current');
                const previousSlideElem = activeSlideElem.previousElementSibling;
                const nextSlideElem = activeSlideElem.nextElementSibling;

                if (previousSlideElem && previousSlideElem.classList.contains('slick-cloned') == false) {
                    this.updateSlideToPeakImage(previousSlideElem, 'previous');
                }

                if (nextSlideElem && nextSlideElem.classList.contains('slick-cloned') == false) {
                    this.updateSlideToPeakImage(nextSlideElem, 'next');
                }
            },

            recalculateSlickSlider() {
                // Recalculate widths to fix issue where lazyloaded items don't have correct widths set on Slides
                this.slickInstance.slick('setPosition');
            },

            lazyLoadSlide(slide) {
                if (!slide) return;

                if (slide.classList.contains('slide-custom-lazy-loaded') || slide.classList.contains('slide-custom-lazy-loading')) return; // Already been lazyloaded

                const slideImg = slide.querySelector('img.asset-img');
                const slideVid = slide.querySelector('video.asset-vid');

                if (slideVid) {
                    this.preloadVideo(slideVid, () => this.recalculateSlickSlider());
                    slide.classList.add('slide-custom-lazy-loading');
                }
                
                if (slideImg) {
                    this.preloadImage(slideImg, () => this.recalculateSlickSlider());
                    slide.classList.add('slide-custom-lazy-loading');
                }

                // Ensure we also lazyload any other clones / that are a copy of this slide
                const itemElem = slide.querySelector('[data-modal-carousel-item-id]');
                if (itemElem) {
                    const itemId = itemElem.getAttribute('data-modal-carousel-item-id');

                    // Cannot use :has as it isnt supported in 15.3, so having to manually traverse
                    const activeModalItems = Array.from(document.querySelectorAll(`.slick-slide [data-modal-carousel-item-id="${itemId}"]`));
                    const allItemSlides = activeModalItems.map(elem => elem.parentElement.parentElement);

                    allItemSlides.forEach(otherSlide => {
                        if (otherSlide != slide) {
                            this.lazyLoadSlide(otherSlide);
                        }
                    });
                }
            },

            lazyLoadSurroundingSlides(modalOpening) {
                const activeSlideElem = this.$refs.slickCarousel.querySelector('.slick-current');
                const previousSlideElem = activeSlideElem.previousElementSibling;
                const nextSlideElem = activeSlideElem.nextElementSibling;

                // Ensure current slide is lazyloaded just in case
                if (activeSlideElem) {
                    this.lazyLoadSlide(activeSlideElem);
                }

                // Load next slides
                if (previousSlideElem) {
                    this.lazyLoadSlide(previousSlideElem);

                    const prevPreviousSlideElem = previousSlideElem.previousElementSibling;
                    this.lazyLoadSlide(prevPreviousSlideElem);
                }

                // Load prev slides
                if (nextSlideElem) {
                    this.lazyLoadSlide(nextSlideElem);

                    const nextNextSlideElem = nextSlideElem.nextElementSibling;
                    this.lazyLoadSlide(nextNextSlideElem);
                }

                // If modal is opening, we will want to check for two call backs and if so then run the peeks
                if (modalOpening) {

                }
            },

            preloadImage(elem, callback) {
                const that = this;
                const srcAttr = elem.getAttribute('data-src-observer');
                if (!srcAttr) return;

                elem.setAttribute('src', srcAttr);

                const sourceSets = elem.parentElement.querySelectorAll('[data-srcset-observer]');
                sourceSets.forEach(source => {
                    const val = source.getAttribute('data-srcset-observer');
                    if (val) {
                        source.setAttribute('srcset', val);
                        source.removeAttribute('data-srcset-observer');
                    }
                });

                elem.onload = function () {
                    elem.removeAttribute('data-src-observer');

                    const closestSlide = elem.closest('.slick-slide');
                    if (closestSlide) {
                        closestSlide.classList.add('slide-custom-lazy-loaded');
                        closestSlide.classList.remove('slide-custom-lazy-loading');
                    }

                    if (callback) {
                        callback();
                    }
                }
            },

            preloadVideo(elem, callback) {
                const sourceSets = elem.querySelectorAll('[data-src-observer]');
                sourceSets.forEach(source => {
                    const val = source.getAttribute('data-src-observer');
                    if (val) {
                        source.setAttribute('src', val);
                        source.removeAttribute('data-src-observer');
                    }
                });

                if (elem.getAttribute('data-poster')) {
                    const posterUrl = elem.getAttribute('data-poster');
                    if (posterUrl) {
                        elem.setAttribute('poster', posterUrl);
                        elem.removeAttribute('data-poster');
                    }
                }

                elem.load();
                elem.pause();

                const closestSlide = elem.closest('.slick-slide');
                if (closestSlide) {
                    closestSlide.classList.add('slide-custom-lazy-loaded');
                    closestSlide.classList.remove('slide-custom-lazy-loading');
                }

                if (callback) {
                    callback();
                }
            },


            onAfterCarouselChange(event, slick, currentSlide) {
                this.lazyLoadSurroundingSlides(false);

                const caption = slick.$slides[currentSlide].querySelector('.gallery-modal__carousel-caption');

                if (caption) {
                    this.carouselActiveItemMessaging = caption.innerText;
                } else {
                    this.carouselActiveItemMessaging = '';
                }

               
                this.calculateCarouselPeeks();                
            },

            onBeforeCarouselChange(event, slick, currentSlide, nextSlide) {
                // Reset the transition on the slide that will be shown so we can see the animation
                if (this.galleryVersion === 'modern') {
                    // setTimeout(() => {
                    slick.$slides[nextSlide].style.transform = '';
                    slick.$slides[nextSlide].classList.remove('has-peek');
                    // }, 100);
                }

                // Pause all videos (including any clones) as they will be hidden
                const customChangeEvent = new CustomEvent('carouselSlideChange', { detail: { state: 'old-slide' } });

                slick.$slideTrack.get(0).querySelectorAll('video').forEach(video => {
                    setTimeout(() => {
                        video.dispatchEvent(customChangeEvent);
                    }, 15);
                });

                // Locate the new slide and if it has a video we want to trigger the start event
                const newSlideVideo = slick.$slides[nextSlide].querySelector('video');
                const customChangeEventStart = new CustomEvent('carouselSlideChange', { detail: { state: 'new-slide' } });
                if (newSlideVideo) {
                    setTimeout(() => {
                        newSlideVideo.dispatchEvent(customChangeEventStart);
                    }, 25);
                }
                return;
            },

            displayHtmLChar(str) {
                if (!str) return '';
                return str.replace(/<\/?[^>]+>/ig, " "); 
            }
        },

        components: {
            galleryVideo
        }
    }
</script>